// components/automations/AutomationCard.js
import React, { useState } from 'react';
import { Card, CardContent, Badge } from '../common';
import { useTranslations } from '../../hooks/useTranslations';
import { useServicesPG } from '../../hooks/useServicesPG';
import { useTemplates } from '../../hooks/useTemplates';

export function AutomationCard({ automation, onEdit, onToggle, onDelete }) {
  const [isToggling, setIsToggling] = useState(false);
  const t = useTranslations();
  const { services } = useServicesPG();
  const { getTemplate } = useTemplates();

  const getTriggerIcon = (type) => {
    const base = 'w-5 h-5 md:w-6 md:h-6';
    switch (type) {
      case 'before_appointment':
        return <svg className={`${base} text-yellow-600`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>;
      case 'after_appointment':
        return <svg className={`${base} text-green-600`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>;
      case 'appointment_created':
        return <svg className={`${base} text-blue-600`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4"/></svg>;
      default:
        return <svg className={`${base} text-gray-600`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>;
    }
  };

  const triggerText = (type, timing) =>
    type === 'before_appointment' ? ` ${timing} ${t.hoursBefore}`
    : type === 'after_appointment'  ? ` ${timing} ${t.hoursAfter}`
    : type === 'appointment_created' ? t.whenCreated
    : type === 'appointment_approved' ? t.whenAppointmentApproved
    : (t.unknown || 'Unknown');

  const templateName = (id) => (getTemplate(id)?.name || id);

  const handleToggle = async () => {
    try {
      setIsToggling(true);
      await onToggle(automation.id);
    } finally {
      setIsToggling(false);
    }
  };

  return (
    <Card
      dir="rtl"
      className={[
        'relative overflow-hidden rounded-2xl',
        'bg-white/70 supports-[backdrop-filter]:bg-white/60 backdrop-blur-sm',
        'border border-white/30 shadow-md shadow-gray-200/40',
        'transition-all duration-300 hover:shadow-lg hover:shadow-gray-300/40 hover:scale-[1.01]',
        automation.isActive ? 'ring-1 ring-emerald-200/70' : ''
      ].join(' ')}
    >
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-white/50 via-transparent to-rose-50/25" />

      <CardContent className="relative p-4 md:p-5">
        {/* Top grid: icon • details • controls */}
        <div className="grid grid-cols-[auto,1fr,auto] items-start gap-3 md:gap-4">
          {/* Icon tile */}
          <div className="w-10 h-10 md:w-12 md:h-12 rounded-xl bg-gradient-to-br from-emerald-50 to-white border border-emerald-100 grid place-items-center shadow-sm">
            {getTriggerIcon(automation.triggerType)}
          </div>

          {/* Details */}
          <div className="min-w-0">
            {/* Title + inline status chip */}
            <div className="flex items-center justify-between gap-2">
              <h3 className="truncate text-base md:text-lg font-semibold text-gray-900 leading-tight">
                {automation.name}
              </h3>
            </div>

            {/* Badges row */}
            <div className="mt-1 flex flex-wrap items-center gap-1.5 text-[12px] md:text-sm text-gray-700">
              <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200 max-w-[70%] truncate">
                {templateName(automation.templateId)}
              </Badge>
            </div>

            {/* Meta */}
            <div className="mt-2 space-y-1">
              <div className="flex items-center gap-1.5 text-[12px] md:text-sm text-gray-600">
                <span className="text-gray-500">{t.trigger}:</span>
                <span className="font-medium">{triggerText(automation.triggerType, automation.timing)}</span>
              </div>
              <div className="flex items-center gap-1.5 text-[12px] md:text-sm">
                <span className="text-gray-500 font-medium">{t.services || 'שירותים'}:</span>
                <span className="text-gray-700 truncate">
                  {automation.getApplicableServices(services)}
                </span>
              </div>
            </div>

            {/* Description */}
            {automation.description && (
              <p className="mt-2 text-[12px] md:text-sm text-gray-600 line-clamp-2">
                {automation.description}
              </p>
            )}
          </div>

          {/* Controls */}
          <div className="flex flex-col items-end gap-2 sm:flex-row sm:items-center sm:gap-3">
            {/* Edit */}
            <button
              onClick={() => onEdit(automation)}
              className="p-2 rounded-lg bg-white/70 border border-gray-200 hover:bg-gray-50 text-gray-500 hover:text-blue-600 shadow-sm transition-colors"
              title={t.edit || 'עריכה'}
              aria-label={t.edit || 'עריכה'}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
            </button>

            {/* (Optional) Delete */}
            {onDelete && (
              <button
                onClick={() => onDelete(automation.id)}
                className="p-2 rounded-lg bg-white/70 border border-gray-200 hover:bg-gray-50 text-gray-500 hover:text-red-600 shadow-sm transition-colors"
                title={t.delete || 'מחיקה'}
                aria-label={t.delete || 'מחיקה'}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6M9 7h6m-7 0a2 2 0 012-2h4a2 2 0 012 2m-8 0h8" />
                </svg>
              </button>
            )}

            {/* Toggle */}
            <div className={`flex items-center gap-2 ${isToggling ? 'opacity-60 pointer-events-none' : ''}`}>
              <span className={`text-[12px] md:text-sm font-medium ${automation.isActive ? 'text-emerald-600' : 'text-gray-500'}`}>
                {automation.isActive ? t.active : t.inactive}
              </span>

              <label className="relative inline-flex items-center cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={automation.isActive}
                  onChange={handleToggle}
                  disabled={isToggling}
                  className="sr-only peer"
                  aria-label={automation.isActive ? (t.deactivate || 'השבת') : (t.activate || 'הפעל')}
                />
                {/* Track */}
                <span className="block w-11 h-6 rounded-full bg-gray-200 transition-colors peer-checked:bg-emerald-500" />
                {/* Knob (absolute, moves both in LTR/RTL) */}
                <span className="pointer-events-none absolute top-0.5 start-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform
                                  peer-checked:translate-x-[20px] rtl:peer-checked:-translate-x-[20px]" />
              </label>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
